import { useState } from "react";
import type { JSX } from "react";
import { IconButton, Tooltip } from "@mui/material";
import CallIcon from "@mui/icons-material/Call";

import { CallLauncher } from "./CallLauncher.js";
import { useCallsEnabled } from "./useCallsEnabled.js";

/**
 * Top-nav call button — sits just before the notification bell in `AppShell`.
 * Opens the `CallLauncher` (pick staff / dial a number / pick a CRM contact,
 * then launch into the same `CallWindow` chat uses). Hidden when calling isn't
 * configured on the server (`useCallsEnabled()` 404 → false), matching the rest
 * of the calling UI. Styled to match `NotificationBell`.
 *
 * Mounted inside `<CallProvider>` (the launcher reads `useCallController`).
 */
export function CallLauncherButton(): JSX.Element | null {
  const callsEnabled = useCallsEnabled();
  const [open, setOpen] = useState(false);

  if (!callsEnabled) return null;

  return (
    <>
      <Tooltip title="Start a call">
        <IconButton
          type="button"
          aria-label="Start a call"
          data-testid="call-launcher-button"
          onClick={() => setOpen(true)}
          sx={{
            height: 40,
            width: 40,
            borderRadius: 1.5,
            color: "text.secondary",
            "&:hover": { bgcolor: "action.hover", color: "text.primary" },
          }}
        >
          <CallIcon sx={{ fontSize: 22 }} aria-hidden />
        </IconButton>
      </Tooltip>
      <CallLauncher open={open} onClose={() => setOpen(false)} />
    </>
  );
}
